import Dinero, { Currency } from 'dinero.js';
import { Cart, RentalCartItem } from '../../../clients/customer-api/cart/cart';
import {
  ExcludeProductFeeRequest,
  FeeLineItem,
  FinancialSummary,
  ProductFeeLineItem,
} from '../../../clients/quipli-service/financials/financials';
import { Card } from '../../../components/card/card';
import { Divider } from '../../../components/divider/divider';
import { Typography } from '../../../components/typography/typography';
import { Location } from '../../../clients/quipli-service/location/location';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Label } from '../../../clients/quipli-service/label/label';
import { Fragment, ReactNode, useState } from 'react';
import { displayHumanDuration } from '../../../util/util';
import Icon from '../../../components/icons/icon';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { useCustomer } from '../../../hooks/use-customer-context';
import { ProductCustomService, ProductOption } from '../../../clients/customer-api/product/customer-product';
import { formatPhoneNumber } from '../../../util/util';
import { useCartCalculations } from './hooks/use-cart-calculations';
import { CheckoutLineItem } from './components/checkout-line-item';
import { CartItemSummary } from './components/cart-item-summary';

export const CheckoutSummary = ({
  cart,
  summary,
  location,
  customizations,
  config,
  labels,
  excludeFeeIds,
  excludeProductFees,
  excludeDamageWaiver,
  setExcludedFeeIds,
  setExcludedProductFees,
  setExcludedDamageWaiver,
  reload,
  isSubmitting,
  isQuote,
}: {
  cart: Cart;
  summary: FinancialSummary;
  location: Location;
  customizations?: Map<number, { options: ProductOption[]; services: ProductCustomService[] }>;
  config: Configuration[];
  labels: Label[];
  excludeFeeIds?: number[];
  excludeProductFees?: ExcludeProductFeeRequest[];
  excludeDamageWaiver?: boolean;
  setExcludedFeeIds?: (ids: number[]) => void;
  setExcludedProductFees?: (fees: ExcludeProductFeeRequest[]) => void;
  setExcludedDamageWaiver?: (exclude: boolean) => void;
  reload: () => void;
  isSubmitting: boolean;
  isQuote?: boolean;
}) => {
  const { updateCartMeta } = useCustomerAPI();
  const customer = useCustomer();

  const [modal, setModal] = useState<ReactNode>();

  const {
    delivery,
    pickup,
    damageWaiver,
    coupon,
    tax,
    taxInclusive,
    taxLabel,
    locationFees,
    productFees,
    additionalDiscount,
    orderSecurityDeposit,
    securityDepositPrice,
    checkoutText,
    hidePrices,
    displayTaxByJurisdiction,
    taxBreakdown,
    deliveryTax,
    pickupTax,
    damageWaiverTax,
    additionalDiscountTax,
    couponTax,
    productFeesTax,
  } = useCartCalculations({
    summary,
    location,
    config,
    labels,
    customer,
  });

  const getDuration = (rentalItem: RentalCartItem) => {
    return rentalItem.endDate ? displayHumanDuration(rentalItem.endDate.diff(rentalItem.startDate)) : undefined;
  };

  return (
    <Card className="rounded-lg shadow-sm flex flex-col px-0">
      <div className="py-4 px-3 flex flex-col gap-5">
        <Typography type="text-md" weight="semi-bold">
          Summary
        </Typography>

        {cart.items
          .sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0))
          .map((item) => {
            const lineItem = summary.lineItems.find((li) => li.type === 'Product' && li.id === item.id);
            const fees = summary.lineItems.filter(
              (li) => li.type === 'ProductFee' && li.productLineItemId === item.id,
            ) as ProductFeeLineItem[] | undefined;

            return (
              <CartItemSummary
                item={item}
                lineItem={lineItem}
                fees={fees}
                cart={cart}
                location={location}
                reload={reload}
                taxInclusive={taxInclusive}
                taxLabel={taxLabel}
                taxBreakdown={taxBreakdown}
                customizations={customizations}
                isSubmitting={isSubmitting}
                setExcludedProductFees={setExcludedProductFees}
                excludeProductFees={excludeProductFees}
                getDuration={getDuration}
                isQuote={isQuote}
                setModal={setModal}
              />
            );
          })}

        <div className="flex flex-col gap-2">
          {locationFees
            .filter((fee) => fee.optional)
            .map((fee, idx) => (
              <div key={`location-fee-${idx}`} className="flex gap-1">
                <Checkbox
                  dataCy={`location-fee-checkbox-${fee.feeId}`}
                  type="ecommercePrimary"
                  isChecked={!excludeFeeIds?.includes(fee.feeId)}
                  label={`${fee.description} (optional)`}
                  isDisabled={isSubmitting}
                  onChange={(isChecked) => {
                    if (!isChecked) {
                      setExcludedFeeIds?.([...(excludeFeeIds || []), fee.feeId]);
                      updateCartMeta(
                        { ...cart.meta, excludeFeeIds: [...(excludeFeeIds || []), fee.feeId] },
                        location.id,
                      );
                    } else {
                      setExcludedFeeIds?.(excludeFeeIds?.filter((id) => id !== fee.feeId) || []);
                      updateCartMeta(
                        { ...cart.meta, excludeFeeIds: excludeFeeIds?.filter((id) => id !== fee.feeId) },
                        location.id,
                      );
                    }
                  }}
                />
              </div>
            ))}
          {damageWaiver?.optional && (
            <div className="flex gap-1">
              <Checkbox
                dataCy="damage-waiver-checkbox"
                type="ecommercePrimary"
                isChecked={!excludeDamageWaiver}
                label={`${damageWaiver.description} (optional)`}
                isDisabled={isSubmitting}
                onChange={(isChecked) => {
                  setExcludedDamageWaiver?.(!isChecked);
                  updateCartMeta({ ...cart.meta, excludeDamageWaiver: !isChecked }, location.id);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <Divider />

      <div className="py-4 flex flex-col gap-2">
        <CheckoutLineItem
          label="Delivery"
          amount={taxInclusive ? delivery.add(deliveryTax) : delivery}
          taxInclusive={taxInclusive}
          taxLabel={taxLabel}
          tax={deliveryTax}
        />

        {pickup && !pickup.isZero() && (
          <CheckoutLineItem
            label="Pickup"
            amount={taxInclusive ? pickup.add(pickupTax) : pickup}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={pickupTax}
          />
        )}

        {coupon && (
          <CheckoutLineItem
            label={`Coupon (${coupon.type === 'TagPricing' ? `Tag{${coupon.code}}` : coupon.code})`}
            amount={taxInclusive ? coupon.amount.add(couponTax) : coupon.amount}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={couponTax}
            dataCy={'coupon-line-item'}
          />
        )}

        {displayTaxByJurisdiction &&
          tax &&
          tax.taxBreakdown?.map((b) => (
            <CheckoutLineItem label={`${b.jurisdiction} - (${b.percentage}%)`} amount={b.amount} />
          ))}

        {!displayTaxByJurisdiction && tax && <CheckoutLineItem label="Tax" amount={tax.amount} />}

        {damageWaiver && damageWaiver.isSelected && (
          <CheckoutLineItem
            label="Damage Waiver"
            amount={taxInclusive ? damageWaiver.amount.add(damageWaiverTax) : damageWaiver.amount}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={damageWaiverTax}
          />
        )}

        {locationFees
          .filter((fee) => !(fee.optional && !fee.isSelected))
          .map((fee, idx) => (
            <Fragment key={`location-fee-${idx}`}>
              <CheckoutLineItem
                label={fee.description}
                amount={fee.amount.add(
                  taxInclusive
                    ? taxBreakdown.reduce((tax, breakdown) => {
                        return tax.add(
                          breakdown.itemizedBreakdown?.find(
                            (b) => b.type === 'Fee' && (b.contractLineItem as FeeLineItem)?.feeId === fee.feeId,
                          )?.tax ?? Dinero({ amount: 0, currency: location.currency as Currency }),
                        );
                      }, Dinero({ amount: 0, currency: location.currency as Currency }))
                    : Dinero({ amount: 0, currency: location.currency as Currency }),
                )}
                taxInclusive={taxInclusive}
                taxLabel={taxLabel}
                tax={taxBreakdown.reduce((tax, breakdown) => {
                  return tax.add(
                    breakdown.itemizedBreakdown?.find(
                      (b) => b.type === 'Fee' && (b.contractLineItem as FeeLineItem)?.feeId === fee.feeId,
                    )?.tax ?? Dinero({ amount: 0, currency: location.currency as Currency }),
                  );
                }, Dinero({ amount: 0, currency: location.currency as Currency }))}
              />
            </Fragment>
          ))}

        {productFees.getAmount() > 0 && (
          <CheckoutLineItem
            label="Product Fees Total"
            amount={taxInclusive ? productFees.add(productFeesTax) : productFees}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={productFeesTax}
          />
        )}

        {additionalDiscount && (
          <CheckoutLineItem
            label="Additional Discount"
            amount={taxInclusive ? additionalDiscount.add(additionalDiscountTax) : additionalDiscount}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={additionalDiscountTax}
          />
        )}

        <div className="m-1 py-2 rounded-md bg-grey-100">
          <CheckoutLineItem
            label="Total"
            amount={!hidePrices ? summary.total : undefined}
            noAmountText="Sign in to see total."
            weigth="bold"
            className="px-2"
          />
        </div>

        {(securityDepositPrice?.getAmount() || 0) > 0 && (
          <div className="flex justify-between px-3">
            <Typography
              type="text-sm"
              weight="semi-bold"
              className="underline text-quipli-black-500"
            >{`Refundable Security Deposit (Hold)${
              orderSecurityDeposit?.percentage ? ` ${orderSecurityDeposit.percentage}%` : ''
            }`}</Typography>
            <Typography type="text-sm" weight="semi-bold" dataCy="security-deposit-price">
              {!hidePrices ? securityDepositPrice.toFormat() : '-'}
            </Typography>
          </div>
        )}

        {checkoutText && (
          <div className="mt-2 px-3">
            <Typography type="text-sm" className="text-quipli-black-500">
              <div dangerouslySetInnerHTML={{ __html: checkoutText }} />
            </Typography>
          </div>
        )}

        {!location.allowDeliverySelection && (
          <div className="mt-2 px-3">
            <Divider />

            <div className="flex items-center gap-2 pt-4">
              <Icon icon="Location01" />
              <div className="flex flex-col">
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-500">
                  {labels.find((label) => label.key === 'M_Pickup_at')?.caption || 'Pickup at'}:
                </Typography>

                <Typography type="text-sm" className="text-quipli-black-500">
                  {`${location.name}, ${location.address.address1}${
                    location.address.address2 ? `, ${location.address.address2}` : ''
                  }${location.address.city ? `, ${location.address.city}` : ''}${
                    location.address.state ? `, ${location.address.state}` : ''
                  }${location.address.zip ? `, ${location.address.zip}` : ''}${
                    location.phone ? `. ${formatPhoneNumber(location.phone)}` : ''
                  }`}
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>

      {modal}
    </Card>
  );
};
